import type { ReactNode } from 'react';

/** Оболочка экрана — ОДНА на всё приложение.
 *
 *  Строка вынесена в константу, потому что повторялась в трёх местах и
 *  начала расходиться: у одного экрана боковые отступы уже отличались от
 *  других. Правка в одном месте — правка везде.
 *
 *  Высота в `dvh`, а не `vh`: `100vh` равен высоте при спрятанных панелях
 *  браузера, и при показанных содержимое уезжает под них (раздел 8.3).
 *
 *  Безопасные поля учитываются со ВСЕХ четырёх сторон. Раньше был только
 *  нижний — и этого хватало ровно до поворота экрана: в альбомной
 *  ориентации вырез камеры и скруглённые углы съедают бок, и кнопка ответа
 *  оказывается наполовину под ними. Ребёнок жмёт и не попадает.
 *
 *  `landscape:` уменьшает отступы: в альбомной ориентации телефона высота
 *  около 375 пикселей, и обычные поля съедают её так, что до кнопок надо
 *  прокручивать.
 *
 *  `md:` даёт больше ширины планшету и ноутбуку родителя. Не «во весь
 *  экран»: строка длиной в полтора метра не читается, а задание должно
 *  ловиться взглядом целиком. */
export const SCREEN_SHELL = 'mx-auto flex min-h-dvh w-full max-w-[30rem] flex-col gap-4 pt-[calc(1.25rem+env(safe-area-inset-top))] pb-[calc(1.25rem+env(safe-area-inset-bottom))] pl-[calc(1rem+env(safe-area-inset-left))] pr-[calc(1rem+env(safe-area-inset-right))] landscape:gap-3 landscape:pt-[calc(0.75rem+env(safe-area-inset-top))] md:max-w-[36rem]';

export function Screen({ children }: { children: ReactNode }) {
  return (
    <main className={SCREEN_SHELL}>
      {children}
    </main>
  );
}

export function Title({ children }: { children: ReactNode }) {
  return <h1 className="m-0 text-2xl font-semibold">{children}</h1>;
}

export function Hint({ children }: { children: ReactNode }) {
  return <p className="m-0 text-[0.95rem] text-muted">{children}</p>;
}

/** Уведомление об итоге действия — вежливая живая область.
 *
 *  Область стоит на экране ВСЕГДА, пустая, а не появляется вместе с текстом.
 *  Вежливую область, вставленную в страницу уже с текстом, браузер не
 *  объявляет: он ещё не знает, что за ней надо следить (MDN, «ARIA live
 *  regions»). Пустая она спрятана `sr-only` — абсолютное положение выводит
 *  её из потока, и лишнего промежутка между строками экрана нет, — но для
 *  вспомогательных технологий существует.
 *
 *  Отказ устроен иначе: `role="alert"` прямо на абзаце, который появляется
 *  только вместе с текстом. Его объявляют и вставленным — исключение,
 *  которое браузеры делают только для этой роли, — а после успеха от отказа
 *  не остаётся ничего. */
export function Notice({ text }: { text: string | null | undefined }) {
  return (
    <div role="status" className={text ? 'text-[0.95rem] text-muted' : 'sr-only'}>
      {text}
    </div>
  );
}

interface ButtonProps {
  children: ReactNode;
  onClick: () => void;
  disabled?: boolean;
  /** Кнопка прижимается к низу экрана, забирая свободное место над собой. */
  bottom?: boolean;
}

export function PrimaryButton({ children, onClick, disabled, bottom }: ButtonProps) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={`${bottom ? 'mt-auto' : ''} rounded-[0.85rem] bg-accent p-4 text-[1.1rem] font-semibold text-white disabled:cursor-not-allowed disabled:bg-[#c7cddb]`}
    >
      {children}
    </button>
  );
}

export function SecondaryButton({ children, onClick, bottom }: ButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`${bottom ? 'mt-auto' : ''} rounded-[0.85rem] border-2 border-line bg-card p-4 text-[1.05rem] font-semibold text-ink`}
    >
      {children}
    </button>
  );
}

/** Карточка списка. Один вид у сессии в истории и у примера в таблице. */
export function Card({ children, onClick }: { children: ReactNode; onClick?: () => void }) {
  const className =
    'w-full rounded-xl border border-line bg-card p-3 text-left text-ink';
  if (!onClick) return <div className={className}>{children}</div>;
  return (
    <button type="button" onClick={onClick} className={className}>
      {children}
    </button>
  );
}
